import { DefaultButton, TextField } from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ISurveybuttonStyles } from '../../consts';
import { LoaderContext } from '../../routes/router';
import { deleteFile, saveSurveySection } from '../../services/survey';
import { ActionsButton } from './actionButtons';
import { VendorSurveyDropzone } from './surveyDropzone';

export interface ICharityFormType {
    charityFirstTextSelect: boolean | any;
    charitySecondTextSelect: boolean | any;
    charityFirstText: string;
    charitySecondText: string;
    charityFile?: any;
}

interface IProps {
    handlePrevStep: () => void;
    activeStepId: number[];
    handleNewStep: () => void;
    setCharityValues: React.Dispatch<React.SetStateAction<ICharityFormType | null>>;
    charityValues: ICharityFormType | null;
}

const formSchema = yup.object().shape({
    charityFirstTextSelect: yup.string().required('This field is required'),
    charitySecondTextSelect: yup.string().required('This field is required'),
    charityFirstText: yup.string().when('charityFirstTextSelect', {
        is: 'true',
        then: yup.string().required('This field is required'),
    }),
    charitySecondText: yup.string().when('charitySecondTextSelect', {
        is: 'true',
        then: yup.string().required('This field is required'),
    }),
    charityFile: yup.array().when('charityFirstTextSelect' || 'charitySecondTextSelect', {
        is: 'true',
        then: yup.array().min(1, 'This field is required').required(),
    }),
});

export const Charity: React.FC<IProps> = ({
    handleNewStep,
    handlePrevStep,
    activeStepId,
    charityValues,
    setCharityValues,
}) => {
    const [showFirstTextField, setShowFirstTextField] = React.useState(false);
    const [showsecondTextField, setShowSecondTextField] = React.useState(false);
    const [_, setTest] = React.useState(false);
    const ctxt = React.useContext(LoaderContext);
    const {
        control,
        formState: { errors },
        setValue,
        handleSubmit,
        getValues,
        reset,
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues:
            charityValues !== null
                ? charityValues
                : {
                      charityFirstTextSelect: '',
                      charityFirstText: '',
                      charitySecondTextSelect: '',
                      charitySecondText: '',
                      charityFile: [],
                  },
        reValidateMode: 'onChange',
    });

    const onSubmit = React.useCallback(
        (data: ICharityFormType) => {
            const newVal = [
                { questionId: 22, answer: data.charityFirstTextSelect },
                { questionId: 23, answer: data.charityFirstText },
                {
                    questionId: 24,
                    answer: data.charityFile ? data.charityFile.toString() : '',
                },
                { questionId: 25, answer: data.charitySecondTextSelect },
                {
                    questionId: 26,
                    answer: data.charitySecondText,
                    isLastQuestion: true,
                },
            ];
            setCharityValues(data);
            ctxt?.setLoaderIsActive(true);
            saveSurveySection(newVal).then(() => {
                handleNewStep();
                ctxt?.setLoaderIsActive(false);
            });
        },
        [errors],
    );

    React.useEffect(() => {
        if (charityValues && charityValues !== ({} as any)) {
            reset(charityValues as any);
            if (charityValues?.charityFirstText?.length) {
                setShowFirstTextField(true);
            }
            if (charityValues?.charitySecondText?.length) {
                setShowSecondTextField(true);
            }
        }
    }, [charityValues]);

    const removeFile = React.useCallback(
        (file: any) => {
            ctxt?.setLoaderIsActive(true);
            deleteFile(file).then(() => {
                const newFiles = [...getValues('charityFile')];
                setValue(
                    'charityFile',
                    newFiles.filter((item) => item !== file.toString()),
                );
                ctxt?.setLoaderIsActive(false);
            });
        },
        [getValues('charityFile')],
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="survey-main">
                <p className="border-b border-black pb-1 mt-[100px]">Question 1</p>
                <h3>
                    Does the vendor facility or parent company donate to or engage in any
                    initiatives to better the local community in which the vendor facility operates?
                </h3>

                <Controller
                    control={control}
                    name="charityFirstTextSelect"
                    render={({ field }) => (
                        <>
                            <DefaultButton
                                text="Yes"
                                onClick={() => {
                                    setValue('charityFirstTextSelect', 'true');
                                    setShowFirstTextField(true);
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'true' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <DefaultButton
                                text="No"
                                onClick={() => {
                                    setValue('charityFirstTextSelect', 'false');
                                    setShowFirstTextField(false);
                                    setValue('charityFirstText', '');
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'false' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <span className="text-red-500">
                                {errors.charityFirstTextSelect?.message}
                            </span>
                        </>
                    )}
                />
                {showFirstTextField && (
                    <div className="border-l border-black pl-5 mt-[30px]">
                        <span className="subQuestion mb-5">
                            Please outline donations or initiatives engaged in
                        </span>
                        <>
                            <Controller
                                control={control}
                                name="charityFirstText"
                                render={({ field }) => (
                                    <TextField
                                        multiline={true}
                                        onChange={field.onChange}
                                        resizable={false}
                                        rows={5}
                                        className="mt-4"
                                        value={field.value}
                                    />
                                )}
                            />
                            <span className="text-red-500">{errors.charityFirstText?.message}</span>
                        </>
                    </div>
                )}
            </div>
            <div className="survey-main">
                <p className="border-b border-black pb-1 mt-[100px]">Question 2</p>
                <h3>
                    Does the vendor facility or parent company contribute socially to the
                    environment or any other social cause?
                </h3>

                <Controller
                    control={control}
                    name="charitySecondTextSelect"
                    render={({ field }) => (
                        <>
                            <DefaultButton
                                text="Yes"
                                onClick={() => {
                                    setValue('charitySecondTextSelect', 'true');
                                    setShowSecondTextField(true);
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'true' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <DefaultButton
                                text="No"
                                onClick={() => {
                                    setValue('charitySecondTextSelect', 'false');
                                    setShowSecondTextField(false);
                                    setValue('charitySecondText', '');
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'false' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <span className="text-red-500">
                                {errors.charityFirstTextSelect?.message}
                            </span>
                        </>
                    )}
                />
                {showsecondTextField && (
                    <div className="border-l border-black pl-5 mt-[30px]">
                        <span className="subQuestion mb-5">
                            Please outline social contributions
                        </span>
                        <>
                            <Controller
                                control={control}
                                name="charitySecondText"
                                render={({ field }) => (
                                    <TextField
                                        multiline={true}
                                        onChange={field.onChange}
                                        resizable={false}
                                        rows={5}
                                        className="mt-4"
                                        value={field.value}
                                    />
                                )}
                            />
                            <span className="text-red-500">
                                {errors.charitySecondText?.message}
                            </span>
                        </>
                    </div>
                )}
            </div>
            <div className="survey-main">
                {(showFirstTextField || showsecondTextField) && (
                    <div className="border-l border-black pl-5 mt-[30px]">
                        <span className="subQuestion mb-5">
                            Please provide any documentation or evidence of donations, initiatives
                            or social contributions (evidence could include membership, newspaper
                            articles, images)
                        </span>
                        <Controller
                            control={control}
                            name="charityFile"
                            render={() => (
                                <VendorSurveyDropzone
                                    questionId={24}
                                    setValue={setValue}
                                    name="charityFile"
                                />
                            )}
                        />
                        {getValues('charityFile') && getValues('charityFile')[0]?.length !== 0 && (
                            <div className="acceptedFile mt-10">
                                <h3 className="mb-5">Accepted files :</h3>
                                {getValues('charityFile')?.map((item: any) => (
                                    <div
                                        key={item}
                                        className="flex items-center p-4 py-2 border justify-between mb-2"
                                    >
                                        <p>{item}</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                removeFile(item);
                                                setTest((prev) => !prev);
                                            }}
                                            className="px-5 py-3 border-l pr-0 text-2xl"
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <span className="text-red-500 mt-3">{errors.charityFile?.message}</span>
                    </div>
                )}
            </div>
            <ActionsButton handlePrevStep={handlePrevStep} activeStepId={activeStepId} />
        </form>
    );
};
